import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  Search, X, ArrowRight, Command,
  LayoutDashboard, Package, Truck, Calculator, BookOpen, TrendingUp,
  Scale, DollarSign, BarChart3, StickyNote, AlertTriangle, Upload,
  Receipt, Users, Star, Database, Activity, MessageSquare, HelpCircle,
  HeartPulse, Settings, Zap, FileText, Eye, Printer
} from "lucide-react";

const ICON_MAP = {
  LayoutDashboard, Package, Truck, Calculator, BookOpen, TrendingUp,
  Scale, DollarSign, BarChart3, StickyNote, AlertTriangle, Upload,
  Receipt, Users, Star, Database, Activity, MessageSquare, HelpCircle,
  HeartPulse, Settings, Zap, FileText, Eye, Printer
};

// All searchable items across the app
const SEARCH_INDEX = [
  // Main Panels
  { label: "Dashboard", path: "/dashboard", icon: "LayoutDashboard", category: "Panel", keywords: ["home", "overview", "main"] },
  { label: "CRM Hub", path: "/crm", icon: "Users", category: "Panel", keywords: ["customer", "partner", "client", "crm", "relationship"] },
  { label: "Product Prices", path: "/products", icon: "Package", category: "Panel", keywords: ["medicine", "drug", "price list", "branded", "generic"] },
  { label: "Shipping Prices", path: "/shipping", icon: "Truck", category: "Panel", keywords: ["shipping", "rate", "courier", "delivery", "weight"] },
  { label: "Shipment Costing", path: "/costing", icon: "Calculator", category: "Panel", keywords: ["cost", "costing", "shipment", "order", "medicine cost", "shipping cost"] },
  { label: "Ledger", path: "/ledger", icon: "BookOpen", category: "Panel", keywords: ["ledger", "account", "dp client", "partner", "price list"] },
  { label: "Sales & Profit", path: "/sales", icon: "TrendingUp", category: "Panel", keywords: ["sales", "profit", "revenue", "sell price", "margin"] },
  { label: "Ledger Reconciliation", path: "/ledger-reconciliation", icon: "Scale", category: "Panel", keywords: ["reconciliation", "reconcile", "balance", "account", "parcel wise", "summary"] },
  { label: "Financial Overview", path: "/financial-overview", icon: "DollarSign", category: "Panel", keywords: ["financial", "finance", "overview", "money", "summary"] },
  { label: "Reports", path: "/reports", icon: "BarChart3", category: "Panel", keywords: ["report", "performance", "analytics", "analysis"] },
  { label: "Error Resolution", path: "/errors", icon: "AlertTriangle", category: "Panel", keywords: ["error", "fix", "resolve", "problem", "missing", "unassigned"] },
  { label: "Upload Center", path: "/upload-center", icon: "Upload", category: "Panel", keywords: ["upload", "import", "excel", "file", "sheet"] },
  { label: "Payment Verification", path: "/payment-verification", icon: "Receipt", category: "Panel", keywords: ["payment", "verify", "verification", "bank", "check"] },
  { label: "Notepad", path: "/notes", icon: "StickyNote", category: "Panel", keywords: ["notes", "notepad", "memo", "write"] },
  { label: "Favorites", path: "/favorites", icon: "Star", category: "Panel", keywords: ["favorite", "starred", "bookmark", "quick access"] },

  // Reports Sub-pages
  { label: "Performance Reports", path: "/reports/performance", icon: "BarChart3", category: "Report", keywords: ["performance", "partner", "dp client", "yearly", "monthly", "comparison"] },
  { label: "Outstanding Balance Report", path: "/reports/outstanding", icon: "BarChart3", category: "Report", keywords: ["outstanding", "balance", "pending", "receivable", "payable"] },
  { label: "Pending Sales Report", path: "/reports/pending-sales", icon: "BarChart3", category: "Report", keywords: ["pending", "sales", "awaiting", "upload"] },

  // Quick Views - Ledger Reconciliation tabs
  { label: "Ledger - Summary View", path: "/ledger-reconciliation?view=summary", icon: "Scale", category: "Quick View", keywords: ["ledger", "summary", "reconciliation"] },
  { label: "Ledger - Account View", path: "/ledger-reconciliation?view=account", icon: "Scale", category: "Quick View", keywords: ["ledger", "account", "parcel", "reconciliation"] },
  { label: "Ledger - Parcel Wise", path: "/ledger-reconciliation?view=parcel", icon: "Scale", category: "Quick View", keywords: ["ledger", "parcel", "wise", "detail"] },

  // Quick Views - Error Resolution tabs
  { label: "Errors - Overview", path: "/errors?tab=overview", icon: "AlertTriangle", category: "Quick View", keywords: ["error", "overview", "summary"] },
  { label: "Errors - Costing Errors", path: "/errors?tab=costing", icon: "AlertTriangle", category: "Quick View", keywords: ["error", "costing", "product", "shipping"] },
  { label: "Errors - Sales Errors", path: "/errors?tab=sales", icon: "AlertTriangle", category: "Quick View", keywords: ["error", "sales", "awaiting"] },
  { label: "Errors - Unassigned", path: "/errors?tab=unassigned", icon: "AlertTriangle", category: "Quick View", keywords: ["error", "unassigned", "no client"] },
  { label: "Errors - No Tracking", path: "/errors?tab=missing_tracking", icon: "AlertTriangle", category: "Quick View", keywords: ["error", "no tracking", "missing tracking"] },

  // Quick Views - Upload Center tabs
  { label: "Upload - Upload Files", path: "/upload-center?tab=upload", icon: "Upload", category: "Quick View", keywords: ["upload", "file", "import"] },
  { label: "Upload - Year Summary", path: "/upload-center?tab=year-summary", icon: "Upload", category: "Quick View", keywords: ["upload", "year", "summary"] },
  { label: "Upload - Pending", path: "/upload-center?tab=pending", icon: "Upload", category: "Quick View", keywords: ["upload", "pending", "queue"] },
  { label: "Upload - History", path: "/upload-center?tab=history", icon: "Upload", category: "Quick View", keywords: ["upload", "history", "log", "past"] },

  // Quick Views - Payment Verification tabs
  { label: "Payments - Partner List", path: "/payment-verification?tab=partner-list", icon: "Receipt", category: "Quick View", keywords: ["payment", "partner", "list"] },
  { label: "Payments - Pending", path: "/payment-verification?tab=pending", icon: "Receipt", category: "Quick View", keywords: ["payment", "pending", "verify"] },
  { label: "Payments - Verified", path: "/payment-verification?tab=verified", icon: "Receipt", category: "Quick View", keywords: ["payment", "verified", "approved"] },

  // Admin
  { label: "User Management", path: "/users", icon: "Users", category: "Admin", keywords: ["user", "management", "admin", "role", "create", "team"] },
  { label: "Data Management", path: "/data-management", icon: "Database", category: "Admin", keywords: ["data", "management", "cleanup", "backup", "database"] },
  { label: "System Health", path: "/health", icon: "HeartPulse", category: "Admin", keywords: ["health", "system", "status", "sync"] },
  { label: "Activity Logs", path: "/logs", icon: "Activity", category: "Admin", keywords: ["activity", "log", "audit", "history", "actions"] },
  { label: "Print Settings", path: "#print-settings", icon: "Printer", category: "Action", keywords: ["print", "settings", "label", "invoice"] },
];

const CATEGORY_COLORS = {
  "Tracking": "bg-amber-500/10 text-amber-600",
  "Panel": "bg-blue-500/10 text-blue-600",
  "Report": "bg-emerald-500/10 text-emerald-600",
  "Quick View": "bg-orange-500/10 text-orange-600",
  "Admin": "bg-purple-500/10 text-purple-600",
  "Action": "bg-slate-500/10 text-slate-600"
};

const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [trackingResults, setTrackingResults] = useState([]);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Live tracking search — queries that look like tracking numbers (4+ alphanumeric)
  const isTrackingLike = /^[A-Za-z0-9-]{4,}$/.test(query.trim());
  useEffect(() => {
    if (!open || !isTrackingLike || !token) {
      setTrackingResults([]);
      return;
    }
    let cancelled = false;
    setTrackingLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/ledger-reconciliation/search?q=${encodeURIComponent(query.trim())}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok && !cancelled) {
          const data = await res.json();
          setTrackingResults((data.tracking_matches || []).slice(0, 6));
        }
      } catch (_e) {
        if (!cancelled) setTrackingResults([]);
      } finally {
        if (!cancelled) setTrackingLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, query, token, isTrackingLike]);

  // Map tracking matches into palette items
  const trackingItems = isTrackingLike
    ? trackingResults.map(t => ({
        label: t.tracking,
        path: `/ledger-reconciliation?view=ledger&tracking=${encodeURIComponent(t.tracking)}&client=${encodeURIComponent(t.client_code || "")}`,
        icon: "Package",
        category: "Tracking",
        keywords: [],
        tracking: t.tracking,
        client_code: t.client_code,
        subtitle: [t.client_code || "No client", t.order_date, t.total_cost ? `₹${Number(t.total_cost).toLocaleString('en-IN')}` : null].filter(Boolean).join(" · ")
      }))
    : [];

  // Filter results
  const staticResults = query.trim().length > 0
    ? SEARCH_INDEX.filter(item => {
        const q = query.toLowerCase();
        const matchLabel = item.label.toLowerCase().includes(q);
        const matchKeywords = item.keywords.some(k => k.includes(q));
        const matchCategory = item.category.toLowerCase().includes(q);
        return matchLabel || matchKeywords || matchCategory;
      }).slice(0, 12)
    : // Show recent/popular panels when empty
      SEARCH_INDEX.filter(i => i.category === "Panel").slice(0, 8);

  const results = [...trackingItems, ...staticResults];

  // Group by category
  const grouped = results.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  // Keyboard shortcut to open
  useEffect(() => {
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setQuery("");
      setSelectedIndex(0);
    }
  }, [open]);

  // Navigate on selection
  const handleSelect = useCallback((item) => {
    setOpen(false);
    setQuery("");
    if (item.path === "#print-settings") return; // handled externally
    if (item.tracking) {
      // ts param forces the reconciliation page to re-run the jump even for
      // the same tracking searched twice in a row
      navigate(`${item.path}&ts=${Date.now()}`);
      return;
    }
    navigate(item.path);
  }, [navigate]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && results[selectedIndex]) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    }
  };

  // Scroll selected into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  // Reset selection on query change
  useEffect(() => { setSelectedIndex(0); }, [query]);

  if (!user) return null;

  return (
    <>
      {/* Search trigger button in header */}
      <button
        onClick={() => setOpen(true)}
        data-testid="global-search-trigger"
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border/50 bg-muted/30 hover:bg-muted/60 transition-all text-muted-foreground hover:text-foreground cursor-pointer group max-w-[280px] w-full"
      >
        <Search className="w-4 h-4 flex-shrink-0 text-muted-foreground/70" />
        <span className="text-sm truncate">Search panels, tracking...</span>
        <kbd className="hidden md:inline-flex ml-auto items-center gap-0.5 px-1.5 py-0.5 rounded bg-background border border-border/60 text-[10px] font-mono text-muted-foreground/60 flex-shrink-0">
          <Command className="w-3 h-3" />K
        </kbd>
      </button>

      {/* Overlay */}
      {open && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh]" onClick={() => setOpen(false)}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          
          {/* Search Panel */}
          <div
            className="relative w-full max-w-xl mx-4 bg-background rounded-xl border border-border shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
            onClick={e => e.stopPropagation()}
            data-testid="global-search-panel"
          >
            {/* Input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
              <Search className="w-5 h-5 text-muted-foreground/60 flex-shrink-0" />
              <input
                ref={inputRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search panels, tracking numbers, actions..."
                className="flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground/50"
                data-testid="global-search-input"
              />
              {query && (
                <button onClick={() => setQuery("")} className="p-1 rounded hover:bg-muted">
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
              <kbd className="hidden sm:flex items-center px-1.5 py-0.5 rounded bg-muted border border-border/60 text-[10px] font-mono text-muted-foreground/50">
                ESC
              </kbd>
            </div>

            {/* Results */}
            <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-2">
              {trackingLoading && isTrackingLike && (
                <div className="px-4 py-1.5 text-[11px] text-muted-foreground/60 animate-pulse" data-testid="tracking-search-loading">
                  Searching tracking numbers…
                </div>
              )}
              {results.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No results found for "{query}"
                </div>
              ) : (
                Object.entries(grouped).map(([category, items]) => (
                  <div key={category}>
                    <div className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                      {category}
                    </div>
                    {items.map(item => {
                      const flatIdx = results.indexOf(item);
                      const IconComp = ICON_MAP[item.icon] || FileText;
                      const isSelected = flatIdx === selectedIndex;
                      return (
                        <button
                          key={item.path}
                          data-index={flatIdx}
                          data-testid={`search-result-${item.path.replace(/[/?=&]/g, '-')}`}
                          onClick={() => handleSelect(item)}
                          onMouseEnter={() => setSelectedIndex(flatIdx)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                            isSelected ? "bg-accent text-accent-foreground" : "hover:bg-muted/50"
                          }`}
                        >
                          <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${CATEGORY_COLORS[category] || "bg-muted"}`}>
                            <IconComp className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{item.label}</div>
                            <div className="text-xs text-muted-foreground/60 truncate">{item.subtitle || item.path.split("?")[0]}</div>
                          </div>
                          {isSelected && <ArrowRight className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {/* Footer hint */}
            <div className="px-4 py-2 border-t border-border flex items-center gap-4 text-[11px] text-muted-foreground/50">
              <span className="flex items-center gap-1"><kbd className="px-1 rounded bg-muted border text-[10px]">↑↓</kbd> Navigate</span>
              <span className="flex items-center gap-1"><kbd className="px-1 rounded bg-muted border text-[10px]">↵</kbd> Open</span>
              <span className="flex items-center gap-1"><kbd className="px-1 rounded bg-muted border text-[10px]">esc</kbd> Close</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalSearch;
